import React, { useState } from 'react';
import { getImageSrc, handleImageError, formatDisplayText } from '../utils/showcaseUtils';
import { useGiftEligibility } from '../hooks/useGiftEligibility';
import GiftSelectionModal from '../../gifts/GiftSelectionModal';
import ProductShowcaseModal from './ProductShowcaseModal';

// Helper function to check if product is a set or combo
const isSetOrCombo = (productCode) => {
  if (!productCode) return false;
  // Check for CKWSET or CmbItm in the product code
  return productCode.includes('CKWSET') || productCode.includes('CmbItm');
};

export default function ProductCard({ product, onProductSelect }) {
  const [imageError, setImageError] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showShowcaseModal, setShowShowcaseModal] = useState(false);
  const { hasGifts, loading } = useGiftEligibility(product.id);

  const handleViewDetails = () => {
    onProductSelect(product);
  };

  const handleViewGifts = () => {
    setShowGiftModal(true);
  };

  const handleShowcase = () => {
    setShowShowcaseModal(true);
  };

  const handleImageLoadError = (e) => {
    setImageError(true);
    handleImageError(e);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-[#19BFB7]/20 overflow-hidden hover:shadow-xl transition-all duration-300 group h-full flex flex-col">
      {/* Product Image */}
      <div className="relative h-56 md:h-48 bg-white overflow-hidden flex-shrink-0">
        {!imageError && product.imageUrl ? (
          <img
            src={getImageSrc(product.imageUrl)}
            alt={product.name || 'Product image'}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={handleImageLoadError}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="text-4xl mb-2">🛍️</div>
              <p className="text-sm">No image</p>
            </div>
          </div>
        )}
        
        {/* BrowsingGroup Badge */}
        {product.BrowsingGroup && (
          <div className="absolute top-2 right-2 bg-[#033EA5] text-white text-xs px-2 py-1 rounded-full">
            {product.BrowsingGroup}
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 md:p-5 flex-1 flex flex-col">
        {/* Product Name */}
        <h3 className="font-bold text-[#011841] text-lg md:text-xl mb-2 line-clamp-2 group-hover:text-[#033EA5] transition-colors">
          {product.name || 'Unnamed Product'}
        </h3>

        {/* USP/Features */}
        {product.USP && (
          <div className="mb-3">
            <p className="text-sm text-[#033EA5] line-clamp-2">
              {formatDisplayText(product.USP, 80)}
            </p>
          </div>
        )}

        {/* Description */}
        {product.description && (
          <div className="mb-4 flex-1">
            <p className="text-sm text-gray-600 line-clamp-3">
              {formatDisplayText(product.description, 120)}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={handleViewDetails}
            className="w-full bg-[#19BFB7] hover:bg-[#28E3DA] text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-300 text-sm"
          >
            👀 Details
          </button>

          {/* Showcase Button - Only for sets/combos */}
          {isSetOrCombo(product.code) && (
            <button
              onClick={handleShowcase}
              className="w-full bg-gradient-to-r from-[#033EA5] to-[#19BFB7] hover:from-[#19BFB7] hover:to-[#033EA5] text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-300 text-sm shadow-md hover:shadow-lg"
            >
              🎬 Showcase
            </button>
          )}

          {/* Gift Button - Only show if product has gifts */}
          {!loading && hasGifts && (
            <button
              onClick={handleViewGifts}
              className="w-full bg-gradient-to-r from-[#19BFB7] to-[#28E3DA] hover:from-[#28E3DA] hover:to-[#19BFB7] text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-300 text-sm shadow-md hover:shadow-lg"
            >
              🎁 View Gift Options
            </button>
          )}
        </div>
      </div>

      {/* Gift Selection Modal (Showcase Version) */}
      {showGiftModal && (
        <GiftSelectionModal
          products={[product]}
          onComplete={() => setShowGiftModal(false)}
          onClose={() => setShowGiftModal(false)}
        />
      )}

      {/* Showcase Modal */}
      {showShowcaseModal && (
        <ProductShowcaseModal
          product={product}
          onClose={() => setShowShowcaseModal(false)}
        />
      )}
    </div>
  );
}
